import { useState } from "react";
import PropTypes from "prop-types";
import {
  Badge,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useDisclosure,
} from "@chakra-ui/react";
import { reviewStatusInfo } from "./verificationStatus";

/**
 * Status badge + Approve / Reject (with reason) / Request Clarification.
 * In clarification mode the parent renders selectable tiles; this bar shows the
 * selected count, an optional message and Send / Cancel.
 */
const ReviewActionBar = ({
  status,
  resubmitted = false,
  busy = false,
  clarificationMode = false,
  selectedCount = 0,
  clarificationMessage = "",
  onClarificationMessageChange,
  onApprove,
  onReject,
  onStartClarification,
  onCancelClarification,
  onSendClarification,
}) => {
  const rejectModal = useDisclosure();
  const [rejectReason, setRejectReason] = useState("");
  const info = reviewStatusInfo(status);

  const submitReject = async () => {
    const ok = await onReject?.(rejectReason.trim());
    if (ok !== false) {
      setRejectReason("");
      rejectModal.onClose();
    }
  };

  return (
    <>
      <Flex justify="space-between" align="center" gap={3} wrap="wrap">
        <Flex align="center" gap={2} wrap="wrap">
          <Badge colorScheme={info.color} fontSize="sm" px={2} py={1}>
            {info.label}
          </Badge>
          {resubmitted && status === "under_review" && (
            <Badge colorScheme="green" variant="outline">
              Resubmitted — review changes
            </Badge>
          )}
        </Flex>

        {clarificationMode ? (
          <Flex gap={2} align="center" wrap="wrap">
            <Text fontSize="sm" color="gray.600">
              {selectedCount} field(s) selected
            </Text>
            <Button size="sm" variant="ghost" onClick={onCancelClarification} isDisabled={busy}>
              Cancel
            </Button>
            <Button size="sm" colorScheme="orange" onClick={onSendClarification} isLoading={busy} isDisabled={!selectedCount}>
              Send Clarification Request
            </Button>
          </Flex>
        ) : (
          <Flex gap={2} wrap="wrap">
            <Button size="sm" colorScheme="green" onClick={onApprove} isLoading={busy} isDisabled={status === "approved"}>
              Approve
            </Button>
            <Button size="sm" colorScheme="orange" variant="outline" onClick={onStartClarification} isDisabled={busy}>
              {status === "clarification_requested" ? "Request Again" : "Request Clarification"}
            </Button>
            <Button size="sm" colorScheme="red" variant="outline" onClick={rejectModal.onOpen} isDisabled={busy || status === "rejected"}>
              Reject
            </Button>
          </Flex>
        )}
      </Flex>

      {clarificationMode && (
        <Textarea
          mt={3}
          size="sm"
          placeholder="Optional message to the vendor (shown above the requested fields)"
          value={clarificationMessage}
          onChange={(e) => onClarificationMessageChange?.(e.target.value)}
        />
      )}

      <Modal isOpen={rejectModal.isOpen} onClose={rejectModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Reject</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={2}>
              The vendor will see this reason.
            </Text>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection"
              isInvalid={!rejectReason.trim()}
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={rejectModal.onClose}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={submitReject} isLoading={busy} isDisabled={!rejectReason.trim()}>
              Reject
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

ReviewActionBar.propTypes = {
  status: PropTypes.string,
  resubmitted: PropTypes.bool,
  busy: PropTypes.bool,
  clarificationMode: PropTypes.bool,
  selectedCount: PropTypes.number,
  clarificationMessage: PropTypes.string,
  onClarificationMessageChange: PropTypes.func,
  onApprove: PropTypes.func,
  onReject: PropTypes.func,
  onStartClarification: PropTypes.func,
  onCancelClarification: PropTypes.func,
  onSendClarification: PropTypes.func,
};

export default ReviewActionBar;
